import { lazy } from 'react';
import type { Capability } from '@dpem/contracts';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { LoginPage } from '@/app/auth/LoginPage';
import { RegisterPage } from '@/app/auth/RegisterPage';
import { RequireAuth } from '@/app/auth/RequireAuth';
import { RequireCapability } from '@/app/auth/RequireCapability';
import { ForbiddenPage } from '@/app/errors/ForbiddenPage';
import { NotFoundPage } from '@/app/errors/NotFoundPage';
import { AppShell } from '@/app/shell/AppShell';
import { DEFAULT_AUTHENTICATED_PATH, PATHS } from './paths';
import { loadSurface, SURFACE_KEYS, type SurfaceKey } from './surfaces';

/**
 * The route table.
 *
 * Two levels, and the split is deliberate. This file owns the *shape* — which
 * top-level paths exist, which are public, which capability each demands, and where
 * the shell sits. Everything below a surface's prefix is a splat the surface routes
 * itself, so WP13–WP17 add and rename their internal routes without touching a file
 * the other four also need.
 *
 * Surfaces are `lazy`, so a viewer who never opens Admin never downloads it. The
 * loader is `loadSurface`, which throws a named error when a surface is absent or
 * malformed; `AppErrorBoundary` inside the shell turns that into a page with the
 * navigation still working.
 */

/**
 * The capabilities each surface prefix accepts — any one of them opens the door.
 *
 * `authoring` takes three because three roles reach it for three different reasons:
 * an author composes, a catalogue manager curates, a reviewer approves. Gating the
 * prefix on `method:author` alone would lock a reviewer out of the governance queue
 * that lives inside it. Each surface gates its own sub-routes precisely; this is
 * only the outer door.
 */
const SURFACE_GATES: Readonly<Record<SurfaceKey, readonly Capability[]>> = {
  explorer: ['method:read'],
  authoring: ['method:author', 'catalog:manage', 'method:review'],
  compare: ['method:read'],
  workshop: ['workshop:facilitate'],
  /*
   * `method:read` is the outer door because every planning read route is
   * `ANY_ROLE`. Generating, curating and baselining need `plan:manage`, and the
   * surface gates those affordances itself — a plan is read by more people than
   * write it, which is the point of a plan.
   */
  plans: ['method:read'],
  admin: ['admin:manage'],
};

const SURFACE_PREFIX: Readonly<Record<SurfaceKey, string>> = {
  explorer: PATHS.explorer,
  authoring: PATHS.authoring,
  compare: PATHS.compare,
  workshop: PATHS.workshop,
  plans: PATHS.plans,
  admin: PATHS.admin,
};

/**
 * One route per surface, mounted as a splat so `/compare` and
 * `/compare/dpem:DPEM/dpem:DPEM-agentic` both land in the compare surface — a
 * splat matches zero segments as well as many, so the nav link and every deep link
 * resolve to the same route.
 *
 * `lazy` wants a module with a default export; `loadSurface` returns the component
 * itself so it can validate the shape and name the surface in its error. Wrapping it
 * back up here keeps that validation and still gives React what it expects.
 */
const surfaceRoutes = (): readonly RouteObject[] =>
  SURFACE_KEYS.map((key) => {
    const Surface = lazy(async () => ({ default: await loadSurface(key) }));
    return {
      path: `${SURFACE_PREFIX[key]}/*`,
      element: (
        <RequireCapability any={SURFACE_GATES[key]}>
          <Surface />
        </RequireCapability>
      ),
    };
  });

/** Exported so the test helper can mount the same table in a memory router. */
export const appRoutes = (): RouteObject[] => [
  { path: PATHS.login, element: <LoginPage /> },
  { path: PATHS.register, element: <RegisterPage /> },
  {
    // Pathless: the shell is a layout, not a URL. Its children carry absolute
    // paths, so no surface has to know it is nested.
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to={DEFAULT_AUTHENTICATED_PATH} replace /> },
      ...surfaceRoutes(),
      { path: PATHS.forbidden, element: <ForbiddenPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const createAppRouter = (): ReturnType<typeof createBrowserRouter> =>
  createBrowserRouter(appRoutes());
